import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  SvgIcon,
  useMediaQuery
} from '@mui/material';
import {  useTheme } from '@mui/material/styles';
import { useRef } from 'react';

import { RouterLink } from 'src/components/router-link';
import { usePathname } from 'src/hooks/use-pathname';
import { useWindowScroll } from 'src/hooks/use-window-scroll';
import { paths } from 'src/paths';
import { TopNavItem } from './top-nav-item';
import { MobileNav } from './mobile-nav';
import { ThemeToggle } from 'src/components/theme-toggle';

interface Item {
  disabled?: boolean;
  external?: boolean;
  popover?: ReactNode;
  path?: string;
  title: string;
  onClick?: (e: React.MouseEvent) => void;
}

const scrollToProducts = (e: React.MouseEvent) => {
  e.preventDefault();
  const element = document.getElementById('features');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};

const items: Item[] = [
  {
    title: 'Features',
    path: '#features',
    external: false, // Only Docs should be external
    onClick: scrollToProducts
  },
  {
    title: 'Contact',
    path: paths.contact,
    external: false,
  },
  {
    title: 'Docs',
    path: 'https://docs.swiftramp.in',
    external: true // Only Docs opens in new tab
  },
];

const TOP_NAV_HEIGHT: number = 64;

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const pathname = usePathname();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [elevate, setElevate] = useState<boolean>(false);
  const offset = 64;
  const delay = 100;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const [showNav, setShowNav] = useState(true);
  const lastScrollY = useRef(0);

  const handleWindowScroll = useCallback(
    (): void => {
      const currentScrollY = window.scrollY;
      if (window.scrollY > offset) {
        setElevate(true);
      } else {
        setElevate(false);
      }
      if (currentScrollY > lastScrollY.current && currentScrollY > offset) {
        // Scrolling down
        setShowNav(false);
      } else {
        // Scrolling up
        setShowNav(true);
      }
      lastScrollY.current = currentScrollY;
    },
    []
  );

  useWindowScroll({
    handler: handleWindowScroll,
    delay
  });

  const handleMobileNavOpen = (): void => {
    setIsMobileNavOpen(true);
  };

  const handleMobileNavClose = (): void => {
    setIsMobileNavOpen(false);
  };

  return (
    <Box
    component="header"
    sx={{
      backgroundColor: 'white', // set white background
      color: 'black', // set black text
      borderBottomColor: 'divider',
      borderBottomStyle: 'solid',
      borderBottomWidth: 1,
      left: 0,
      position: 'fixed',
      top: showNav ? 0 : `-${TOP_NAV_HEIGHT + 20}px`, // Slide up when hidden
      width: '100%',
      zIndex: (theme) => theme.zIndex.appBar,
      transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1)',
      ...(elevate && {
        boxShadow: (theme) => theme.shadows[3]
      })
    }}
  >
  
      <Container maxWidth="lg">
        <Stack
          direction="row"
          spacing={2}
          sx={{ height: TOP_NAV_HEIGHT }}
        >
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
            sx={{ flexGrow: 1 }}
          >
            <Stack
              alignItems="center"
              component={RouterLink}
              direction="row"
              display="inline-flex"
              href={paths.index}
              spacing={1}
              sx={{ textDecoration: 'none' }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  height: 60,
                  width: 'auto'
                }}
              >
                <Box
                  component="img"
                  src="/assets/swift-ramp-logo.png"
                  alt="SwiftRamp"
                  sx={{
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain',
                   
                  }}
                />
              </Box>
            </Stack>
          </Stack>

          {mdUp && (
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
            >
              <Box
                component="nav"
                sx={{ height: '100%' }}
              >
                <Stack
                  component="ul"
                  alignItems="center"
                  justifyContent="center"
                  direction="row"
                  spacing={1}
                  sx={{
                    height: '100%',
                    listStyle: 'none',
                    m: 0,
                    p: 0
                  }}
                >
                  <>
                    {items.map((item) => {
                      const checkPath = !!(item.path && pathname);
                      const partialMatch = checkPath ? pathname.includes(item.path!) : false;
                      const exactMatch = checkPath ? pathname === item.path : false;
                      const active = item.popover ? partialMatch : exactMatch;


                    

                      return (
                        <TopNavItem
                          active={active}
                          external={item.external}
                          key={item.title}
                          path={item.path} 
                          popover={item.popover}
                          title={item.title}
                          onClick={item.onClick} 
                        />
                      );
                    })}
                  </>
                </Stack>
              </Box>
              <ThemeToggle />
            </Stack>
          )}
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ flexGrow: 1 }}
          >
            <Button
              component={RouterLink}
              href={paths.dashboard.index}
              sx={{
                color: 'black',
                fontWeight: 600
              }}
            >
              Log in
            </Button>
            <Button
              component="a"
              size={mdUp ? 'medium' : 'small'}
              href="https://cal.com/support-team-qsszsx"
              target="_blank"
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600
              }}
            >
              Talk to us
            </Button>
            {!mdUp && (
              <IconButton onClick={handleMobileNavOpen}>
                <SvgIcon fontSize="small">
                  <Menu01Icon />
                </SvgIcon>
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Container>
      <MobileNav
        items={items}
        onClose={handleMobileNavClose}
        open={isMobileNavOpen}
      />
    </Box>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func
};
